import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

export const CHART_COLORS = {
  newCapacity: {
    primary: '#10b981', // Green for growth
    light: 'rgba(16, 185, 129, 0.6)',
    fill: 'rgba(16, 185, 129, 0.2)'
  },
  growthRate: {
    primary: '#8b5cf6', // Purple for acceleration
    light: 'rgba(139, 92, 246, 0.6)',
    fill: 'rgba(139, 92, 246, 0.1)'
  },
  cagr: {
    primary: '#f59e0b', // Orange for trend
    light: 'rgba(245, 158, 11, 0.6)',
    dash: [8, 4]
  },
  target: {
    primary: '#ef4444', // Red for target line
    dash: [5, 5]
  }
} as const;

export interface SolarPVRow {
  year: number;
  sector: string;
  total_watts: number;
  geoid: string;
}

export interface PopulationRow {
  year: number;
  population: number;
  geofips: string;
}

export interface SolarGrowthYearData {
  year: number;
  cumulativeCapacityKw: number;
  newCapacityKw: number;
  growthRatePercent: number;
  fiveYearCAGR: number;
}

export interface SolarAccelerationMetrics {
  latestYearGrowthRate: number;
  fiveYearCAGR: number;
  peakGrowthYear: number;
  peakGrowthRate: number;
  projected2030Capacity: number;
  target2030Capacity: number;
  onTrackToTarget: boolean;
  targetGapKw: number;
  earliestYear: number;
  latestYear: number;
  totalCapacityGrowth: number;
}

export interface SolarAccelerationData {
  chartData: any;
  chartOptions: any;
  yearlyData: SolarGrowthYearData[];
  metrics: SolarAccelerationMetrics;
  narrative: string;
  location: string;
}

export class SolarAccelerationService {
  // California SB 100: 100% clean energy by 2045
  // Solar target: ~60% of electricity from renewables by 2030
  // Estimate: 50 GW of solar capacity by 2030 statewide (conservative)
  private static readonly CA_SOLAR_TARGET_2030_MW = 50000; // 50 GW in MW
  private static readonly CA_POPULATION_2023 = 39000000; // Approximate

  static async getData(location: string): Promise<SolarAccelerationData> {
    await supabaseLocationService.initialize();
    const county = supabaseLocationService.getCountyForCity(location);

    if (!county) {
      throw new Error(`County not found for location: ${location}`);
    }

    const geofips = await supabaseLocationService.getGeofips(location);

    if (!geofips) {
      throw new Error(`Geofips not found for location: ${location}`);
    }

    const [solarData, populationData] = await Promise.all([
      this.fetchSolarData(geofips),
      this.fetchPopulationData(geofips)
    ]);

    const yearlyData = this.processYearlyData(solarData, populationData);
    const metrics = this.calculateMetrics(yearlyData, location, populationData);
    const narrative = this.generateNarrative(location, metrics, yearlyData);

    const chartData = this.generateChartData(yearlyData, metrics);
    const chartOptions = this.generateChartOptions(location);

    return {
      chartData,
      chartOptions,
      yearlyData,
      metrics,
      narrative,
      location
    };
  }

  private static async fetchSolarData(geofips: number): Promise<SolarPVRow[]> {
    const { data, error } = await supabaseClient
      .from('all_pv_size')
      .select('year, sector, total_watts, geoid')
      .eq('geoid', geofips)
      .order('year', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch solar data: ${error.message}`);
    }

    return data || [];
  }

  private static async fetchPopulationData(geofips: number): Promise<PopulationRow[]> {
    const { data, error } = await supabaseClient
      .from('population')
      .select('year, population, geofips')
      .eq('geofips', geofips)
      .order('year', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch population data: ${error.message}`);
    }

    return data || [];
  }

  private static processYearlyData(
    solarData: SolarPVRow[],
    populationData: PopulationRow[]
  ): SolarGrowthYearData[] {
    // Aggregate solar capacity by year
    const yearMap = new Map<number, { cumulativeKw: number }>();

    solarData.forEach(row => {
      const year = parseInt(row.year.toString());
      if (year > 2024) return;

      if (!yearMap.has(year)) {
        yearMap.set(year, { cumulativeKw: 0 });
      }

      const yearData = yearMap.get(year)!;
      yearData.cumulativeKw += (parseFloat(row.total_watts.toString()) || 0) / 1000;
    });

    const sortedYears = Array.from(yearMap.keys()).sort((a, b) => a - b);

    // Calculate growth metrics for each year
    const growthData: SolarGrowthYearData[] = sortedYears.map((year, index) => {
      const currentCapacity = yearMap.get(year)!.cumulativeKw;
      const previousCapacity = index > 0 ? yearMap.get(sortedYears[index - 1])!.cumulativeKw : 0;

      const newCapacityKw = currentCapacity - previousCapacity;
      const growthRatePercent = previousCapacity > 0
        ? ((newCapacityKw / previousCapacity) * 100)
        : 0;

      // Calculate 5-year CAGR if we have enough history
      let fiveYearCAGR = 0;
      if (index >= 5) {
        const fiveYearsAgoCapacity = yearMap.get(sortedYears[index - 5])!.cumulativeKw;
        if (fiveYearsAgoCapacity > 0) {
          fiveYearCAGR = (Math.pow(currentCapacity / fiveYearsAgoCapacity, 1 / 5) - 1) * 100;
        }
      }

      return {
        year,
        cumulativeCapacityKw: currentCapacity,
        newCapacityKw,
        growthRatePercent,
        fiveYearCAGR
      };
    });

    return growthData;
  }

  private static calculateMetrics(
    yearlyData: SolarGrowthYearData[],
    location: string,
    populationData: PopulationRow[]
  ): SolarAccelerationMetrics {
    if (yearlyData.length === 0) {
      return {
        latestYearGrowthRate: 0,
        fiveYearCAGR: 0,
        peakGrowthYear: 0,
        peakGrowthRate: 0,
        projected2030Capacity: 0,
        target2030Capacity: 0,
        onTrackToTarget: false,
        targetGapKw: 0,
        earliestYear: 0,
        latestYear: 0,
        totalCapacityGrowth: 0
      };
    }

    const latestYear = yearlyData[yearlyData.length - 1];
    const earliestYear = yearlyData[0];

    // Find peak growth year
    const peakGrowth = yearlyData.reduce((max, year) =>
      year.growthRatePercent > max.growthRatePercent ? year : max
    , yearlyData[0]);

    // Calculate 2030 projection using latest 5-year CAGR
    const currentCapacity = latestYear.cumulativeCapacityKw;
    const yearsTo2030 = 2030 - latestYear.year;
    const projectionCAGR = latestYear.fiveYearCAGR || 0;
    const projected2030Capacity = currentCapacity * Math.pow(1 + projectionCAGR / 100, yearsTo2030);

    // Calculate location's proportional 2030 target based on California SB 100
    const latestPop = populationData.find(p => parseInt(p.year.toString()) === latestYear.year);
    const locationPopulation = latestPop ? parseFloat(latestPop.population.toString()) : 0;
    const target2030Capacity = locationPopulation > 0
      ? (locationPopulation / this.CA_POPULATION_2023) * this.CA_SOLAR_TARGET_2030_MW * 1000 // Convert MW to kW
      : currentCapacity * 2; // Fallback: double current capacity

    const targetGapKw = target2030Capacity - projected2030Capacity;
    const onTrackToTarget = projected2030Capacity >= target2030Capacity;

    return {
      latestYearGrowthRate: latestYear.growthRatePercent,
      fiveYearCAGR: latestYear.fiveYearCAGR,
      peakGrowthYear: peakGrowth.year,
      peakGrowthRate: peakGrowth.growthRatePercent,
      projected2030Capacity,
      target2030Capacity,
      onTrackToTarget,
      targetGapKw,
      earliestYear: earliestYear.year,
      latestYear: latestYear.year,
      totalCapacityGrowth: latestYear.cumulativeCapacityKw - earliestYear.cumulativeCapacityKw
    };
  }

  private static generateNarrative(
    location: string,
    metrics: SolarAccelerationMetrics,
    yearlyData: SolarGrowthYearData[]
  ): string {
    if (yearlyData.length === 0) {
      return `No solar PV data available for ${location}.`;
    }

    const onTrackStatus = metrics.onTrackToTarget
      ? 'on track to exceed'
      : 'falling short of';

    const gapText = metrics.onTrackToTarget
      ? `surpassing the target by ${Math.abs(metrics.targetGapKw).toLocaleString()} kW`
      : `needing an additional ${Math.abs(metrics.targetGapKw).toLocaleString()} kW to meet the goal`;

    return `${location}'s solar adoption has shown significant momentum, with a 5-year compound annual growth rate of ${metrics.fiveYearCAGR.toFixed(1)}% as of ${metrics.latestYear}. ` +
      `The peak acceleration occurred in ${metrics.peakGrowthYear}, when installations grew ${metrics.peakGrowthRate.toFixed(1)}% year-over-year. ` +
      `At the current growth trajectory, ${location} is projected to reach ${(metrics.projected2030Capacity / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MW of solar capacity by 2030, ` +
      `${onTrackStatus} its proportional share of California's SB 100 clean energy target (${(metrics.target2030Capacity / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MW), ` +
      `${gapText}. This growth pattern reflects ${location}'s commitment to accelerating renewable energy deployment in alignment with state climate goals.`;
  }

  private static generateChartData(
    yearlyData: SolarGrowthYearData[],
    metrics: SolarAccelerationMetrics
  ): any {
    const labels = yearlyData.map(d => d.year.toString());

    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: 'Annual New Capacity (kW)',
          data: yearlyData.map(d => d.newCapacityKw),
          backgroundColor: CHART_COLORS.newCapacity.fill,
          borderColor: CHART_COLORS.newCapacity.primary,
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          type: 'line' as const,
          label: 'Year-over-Year Growth Rate (%)',
          data: yearlyData.map(d => d.growthRatePercent),
          borderColor: CHART_COLORS.growthRate.primary,
          backgroundColor: CHART_COLORS.growthRate.fill,
          fill: false,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1',
          borderWidth: 2
        },
        {
          type: 'line' as const,
          label: '5-Year CAGR Trend (%)',
          data: yearlyData.map(d => d.fiveYearCAGR || null),
          borderColor: CHART_COLORS.cagr.primary,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 6,
          yAxisID: 'y1',
          borderWidth: 3,
          borderDash: CHART_COLORS.cagr.dash,
          spanGaps: true
        }
      ]
    };
  }

  private static generateChartOptions(location: string): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false
      },
      plugins: {
        title: {
          display: true,
          text: `Solar Growth Momentum - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            font: { size: 11 }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              if (label.includes('Capacity')) {
                return `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW`;
              } else if (label.includes('%')) {
                return `${label}: ${value.toFixed(1)}%`;
              }
              return `${label}: ${value}`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
            font: { size: 14, weight: 'bold' as const }
          },
          grid: {
            display: false
          }
        },
        y: {
          type: 'linear' as const,
          position: 'left' as const,
          title: {
            display: true,
            text: 'Annual New Capacity (kW)',
            font: { size: 13, weight: 'bold' as const }
          },
          beginAtZero: true,
          ticks: {
            callback: (value: any) => `${value.toLocaleString()} kW`
          }
        },
        y1: {
          type: 'linear' as const,
          position: 'right' as const,
          title: {
            display: true,
            text: 'Growth Rate (%)',
            font: { size: 13, weight: 'bold' as const }
          },
          beginAtZero: true,
          grid: {
            drawOnChartArea: false
          },
          ticks: {
            callback: (value: any) => `${value.toFixed(0)}%`
          }
        }
      }
    };
  }
}
